import { Button } from "@/shared/components/ui/button";
import { Download } from "lucide-react";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import { useToast } from "@/shared/hooks/use-toast";
import { useEffect } from "react";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import {
  OfferDocument,
  useVbsOfferPdfDownloadMutation,
} from "@/services/backend/vbs/offers/api/offer-api";
import {
  ContractDocument,
  useVbsContractDownloadPDFMutation,
} from "@/services/backend/vbs/contracts/api/contract-api";
import { isZeroUUID } from "@/shared/lib/utilities/uuid";

export function DownloadButton({
  offerId,
  document: documentType,
  text,
}: {
  offerId: string;
  document: OfferDocument;
  text: string;
}) {
  const [download, { isLoading, error, data, reset }] =
    useVbsOfferPdfDownloadMutation();
  const { toast } = useToast();

  useDownloadPdf(data, reset);

  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
    }
  }, [toast, error]);

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({ id: offerId, document: documentType });
  };

  return (
    <Button variant="outline" className="justify-start" onClick={doDownload}>
      {isLoading ? (
        <Download className="mr-4 h-5 w-5 animate-bounce" />
      ) : (
        <Download className="mr-4 h-5 w-5" />
      )}
      <span>{text}</span>
    </Button>
  );
}

export function DownloadContractButton({
  contractId,
  document: documentType,
  text,
}: {
  contractId: string;
  document: ContractDocument;
  text: string;
}) {
  const [download, { isLoading, error, data, reset }] =
    useVbsContractDownloadPDFMutation();
  const { toast } = useToast();

  useDownloadPdf(data, reset);

  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
    }
  }, [toast, error]);

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({
      id: contractId,
      document: documentType,
    });
  };

  return (
    <Button
      variant="outline"
      className="justify-start"
      onClick={doDownload}
      disabled={isLoading || isZeroUUID(contractId)}
    >
      {isLoading ? (
        <Download className="mr-4 h-5 w-5 animate-bounce" />
      ) : (
        <Download className="mr-4 h-5 w-5" />
      )}
      <span>{text}</span>
    </Button>
  );
}
